import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { FormInput, FormSelect, FormTextarea, FormCheckbox } from '@/components/forms/FormField';
import { FormSection, FormActions } from '@/components/forms/Form';
import { useCreateTimeEntry, useUpdateTimeEntry } from './hooks/useTimeEntries';
import type { TimeEntry } from './hooks/useTimeEntries';
import { useJobsList } from '@/features/jobs/hooks/useJobs';
import { useToast } from '@/components/ui/Toast';
import { JobSearchModal, type JobSearchResult } from '@/components/JobSearchModal';

// ─── Validation Schema ───────────────────────────────────────────────────────

const timeEntrySchema = z.object({
  date: z
    .string()
    .min(1, 'Date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
  jobId: z.string().min(1, 'Job is required'),
  taskId: z.string().optional(),
  durationMinutes: z
    .number({
      required_error: 'Duration is required',
      invalid_type_error: 'Duration must be a number',
    })
    .min(1, 'Minimum duration is 1 minute')
    .max(1440, 'Maximum duration is 24 hours (1440 minutes)'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
  isBillable: z.boolean(),
});

type TimeEntryFormValues = z.infer<typeof timeEntrySchema>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function minutesToHoursDisplay(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface TimeEntryFormProps {
  isOpen: boolean;
  onClose: () => void;
  editEntry?: TimeEntry | null;
}

export function TimeEntryForm({ isOpen, onClose, editEntry }: TimeEntryFormProps) {
  const createEntry = useCreateTimeEntry();
  const updateEntry = useUpdateTimeEntry();
  const { data: jobsData } = useJobsList({ pageSize: 100 });
  const { toast } = useToast();
  const [showJobSearch, setShowJobSearch] = useState(false);
  const [selectedJobLabel, setSelectedJobLabel] = useState<string>(editEntry?.jobId ? '' : '');

  const isEditing = !!editEntry;

  const {
    handleSubmit,
    watch,
    reset,
    control,
    setValue,
    formState: { isSubmitting },
  } = useForm<TimeEntryFormValues>({
    resolver: zodResolver(timeEntrySchema),
    defaultValues: {
      date: editEntry?.date ?? new Date().toISOString().split('T')[0],
      jobId: editEntry?.jobId ?? '',
      taskId: editEntry?.taskId ?? '',
      durationMinutes: editEntry?.durationMinutes ?? undefined,
      description: editEntry?.description ?? '',
      isBillable: editEntry?.isBillable ?? true,
    },
  });

  const watchedDuration = watch('durationMinutes');
  const watchedBillable = watch('isBillable');
  const watchedJobId = watch('jobId');

  // Find selected job for hourly rate display
  const selectedJob = jobsData?.items.find((j) => j.id === watchedJobId);

  // Resolve label for editing scenario
  const jobDisplayLabel =
    selectedJobLabel || (selectedJob ? `${selectedJob.title} – ${selectedJob.clientName}` : '');

  const handleJobSelected = (job: JobSearchResult) => {
    setValue('jobId', job.id, { shouldValidate: true });
    setSelectedJobLabel(`${job.title} – ${job.clientName}`);
    setShowJobSearch(false);
  };

  const onSubmit = async (data: TimeEntryFormValues) => {
    try {
      if (isEditing && editEntry) {
        await updateEntry.mutateAsync({
          id: editEntry.id,
          jobId: data.jobId,
          taskId: data.taskId || null,
          date: data.date,
          durationMinutes: data.durationMinutes,
          description: data.description || null,
          isBillable: data.isBillable,
        });
        toast({ variant: 'success', title: 'Time entry updated.' });
      } else {
        await createEntry.mutateAsync({
          jobId: data.jobId,
          taskId: data.taskId || undefined,
          date: data.date,
          durationMinutes: data.durationMinutes,
          description: data.description || undefined,
          isBillable: data.isBillable,
        });
        toast({ variant: 'success', title: 'Time entry created.' });
      }
      reset();
      onClose();
    } catch {
      toast({ variant: 'error', title: 'Failed to save time entry.' });
    }
  };

  const handleClose = () => {
    reset();
    setSelectedJobLabel('');
    onClose();
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        title={isEditing ? 'Edit Time Entry' : 'New Time Entry'}
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormSection>
            {/* Date */}
            <FormInput name="date" control={control} label="Date" type="date" />

            {/* Job Selection */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Job
              </label>
              <button
                type="button"
                onClick={() => setShowJobSearch(true)}
                className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-brand-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-brand-400"
              >
                <span
                  className={jobDisplayLabel ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}
                >
                  {jobDisplayLabel || 'Search & select a job...'}
                </span>
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
              {!watchedJobId && <p className="mt-1 text-xs text-red-500">Job is required</p>}
            </div>

            {/* Task Selection (optional) */}
            <FormSelect
              name="taskId"
              control={control}
              label="Task (optional)"
              options={[{ value: '', label: 'No specific task' }]}
            />

            {/* Duration */}
            <div>
              <FormInput
                name="durationMinutes"
                control={control}
                label="Duration (minutes)"
                type="number"
                min={1}
                max={1440}
                placeholder="e.g. 90"
              />
              {watchedDuration && watchedDuration > 0 && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  = {minutesToHoursDisplay(watchedDuration)}
                </p>
              )}
            </div>

            {/* Description */}
            <FormTextarea
              name="description"
              control={control}
              label="Description"
              placeholder="What did you work on?"
              rows={3}
            />

            {/* Billable */}
            <div className="flex items-center justify-between">
              <FormCheckbox name="isBillable" control={control} label="Billable" />
              {watchedBillable && selectedJob?.budgetAmount && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Rate: ${(selectedJob.budgetAmount / (selectedJob.estimatedHours ?? 1)).toFixed(0)}
                  /hr
                </span>
              )}
            </div>
          </FormSection>

          <FormActions>
            <Button variant="secondary" onClick={handleClose} type="button">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || createEntry.isPending || updateEntry.isPending}
            >
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Entry' : 'Create Entry'}
            </Button>
          </FormActions>
        </form>
      </Modal>

      {/* Job Search Modal — rendered outside parent Modal to avoid z-index/overflow clipping */}
      {showJobSearch && (
        <JobSearchModal
          onSelect={handleJobSelected}
          onClose={() => setShowJobSearch(false)}
          title="Select Job for Time Entry"
          defaultStatus=""
        />
      )}
    </>
  );
}
